import { isClosureSequencePlaying } from "@/lib/shared/closureSequence"
import { useGetAccountId } from "@/lib/useGetAccountId"
import { useLocalStorage, usePreferredReducedMotion } from "@vueuse/core"
import { onBeforeUnmount, ref, watch } from "vue"

import { useMoveOutTimeline } from "./useMoveOutTimeline"

export type ClosurePhase = "pending" | "closing" | "closed"

// Milliseconds from the start of the sequence. On "closed" the timeline and
// the cards fade out together over 300ms, then the closed alert comes in.
const PHASE_STARTS: [ClosurePhase, number][] = [
  ["closing", 1000],
  ["closed", 2500],
]

// Plays the closure sequence once per account, the first time Account Home
// loads after the move-out closed. Customers who prefer reduced motion get
// the closed state straight away.
export const useClosureSequence = () => {
  const { accountId } = useGetAccountId()
  const { isClosed, isLoading } = useMoveOutTimeline()
  const reducedMotion = usePreferredReducedMotion()
  const seenAccounts = useLocalStorage<Record<number, boolean>>(
    "moveOutClosureSeen",
    {},
  )

  const phase = ref<ClosurePhase>()
  const timers: ReturnType<typeof setTimeout>[] = []

  const setPhase = (next: ClosurePhase) => {
    phase.value = next
    // The nav switches to the closed account's items as the cards go.
    if (next === "closed") isClosureSequencePlaying.value = false
  }

  const start = () => {
    // Marked up front so leaving or reloading part-way doesn't replay it.
    seenAccounts.value = { ...seenAccounts.value, [accountId.value]: true }
    isClosureSequencePlaying.value = true
    phase.value = "pending"

    PHASE_STARTS.forEach(([next, delay]) =>
      timers.push(setTimeout(() => setPhase(next), delay)),
    )
  }

  watch(
    isClosed,
    (closed) => {
      if (
        !closed ||
        phase.value ||
        seenAccounts.value[accountId.value] ||
        reducedMotion.value === "reduce"
      ) {
        return
      }

      start()
    },
    { immediate: true },
  )

  onBeforeUnmount(() => {
    timers.forEach(clearTimeout)
    isClosureSequencePlaying.value = false
  })

  return { phase, isLoading }
}
